import type { MedplumClient } from "@medplum/core";
import type {
  DocumentReference,
  Extension,
  Location,
  Organization,
  Practitioner,
  Questionnaire,
  Reference,
  Resource,
  Schedule,
} from "@medplum/fhirtypes";
import {
  FhirResource,
  type SearchOptions,
  type SubscriptionCallback,
} from "@vibrant/models/fhir/FhirResource";
import type { ResourceData, ResourcePatch } from "@vibrant/models/fhir/fhir-resource-types";
import { findIdentifier } from "@vibrant/models/fhir/fhir-resource-utils";
import {
  resolveManagedChildEvents,
  resolveManagedParentEvent,
} from "@/data/scheduling/managed-event-types";
import {
  type AvailabilityFormValues,
  type AvailabilityScheduleInput,
  AvailabilityScheduleInputSchema,
  buildAvailabilityFormValuesFromScheduleInput,
} from "@/lib/cal/availability-mapper";
import {
  CAL_EXTENSION_URL_EVENT_TYPE_SLUG,
  CAL_EXTENSION_URL_EVENT_TYPE_TITLE,
  CAL_IDENTIFIER_SYSTEM_TEAM_EVENT_TYPES,
  type CalClient,
  type CalEventType,
} from "@/lib/cal/client";
import { maybeLocationTypeFromCalLocation } from "@/lib/calendar/utils";
import { findExtension } from "../utils";
import type { Practice } from "./Practice";

/**
 * Extension value type for CodeableConcept with coded items (surveys, hint charge items).
 */
interface CodeableConceptExtensionValue {
  coding: Array<{
    system: string;
    code: string;
    display?: string;
  }>;
}

/**
 * Extension types used in PracticeAppointmentType updates.
 */
type PracticeAppointmentTypeExtension =
  | { url: string; valueString: string }
  | { url: string; valueString: null }
  | { url: string; valueInteger: number }
  | { url: string; valueInteger: null }
  | { url: string; valueBoolean: boolean }
  | { url: string; valueBoolean: null }
  | { url: string; valueCode: string }
  | { url: string; valueCode: null }
  | { url: string; valueCodeableConcept: CodeableConceptExtensionValue }
  | { url: string; valueCodeableConcept: null };

/**
 * Mapping between Cal.com user IDs and child event type IDs.
 */
interface CalChildEventTypeMapping {
  calUserId: number;
  eventTypeId: number;
}

interface CalRoundRobinEventTypeMapping {
  eventTypeId: number;
  slug: string;
  /**
   * Fingerprint of the provider ids that were synced as Cal hosts at last write. Booking-time
   * prepare compares this against the appointment type's *current* provider ids to detect drift
   * (a provider added/removed without a resync since) and force a resync before booking, instead
   * of silently booking against a stale host list. Absent on mappings written before this field
   * existed — treated as always-stale so they resync once, then stay current.
   */
  providerIdsFingerprint?: string;
}

interface DerivedAvailabilityScheduleMapping {
  calUserId: number;
  scheduleId: number;
  fingerprint?: string;
}

/**
 * Actor types used in PracticeAppointmentType (Organization, Practitioner, Location).
 * Note: FHIR Schedule.actor doesn't include Organization in its type union, but we use it
 * for practice association, so we define our own union here.
 */
type ScheduleActorReference =
  | Reference<Organization>
  | Reference<Practitioner>
  | Reference<Location>;

/**
 * Patch type for PracticeAppointmentType updates.
 */
type PracticeAppointmentTypePatch = Omit<ResourcePatch<Schedule>, "actor"> & {
  extension?: Extension[];
  actor?: ScheduleActorReference[];
};

import type { FormValues } from "@calcom/atoms/dist/packages/features/eventtypes/lib/types";
import {
  deduplicateHintChargeItemReferences,
  type HintChargeItemReference,
} from "@/lib/hint/charge-items";
import { createLogger } from "@/lib/logger";
import { Document } from "../files/Document";
import { Survey } from "../questionnaires/Survey";

const logger = createLogger("PracticeAppointmentType");

/** Ids per `_id` search. Keeps the query string under the ~8KB request-line limit. */
const ID_CHUNK_SIZE = 100;

export const VIBRANT_IDENTIFIER_SYSTEM_PRACTICE_ID = "vibrant.practice.id";
export const VIBRANT_IDENTIFIER_SYSTEM_SCHEDULE_TYPE = "vibrant.schedule-type";

export interface PracticeAppointmentTypeQuery {
  practiceId?: string;
  /**
   * Filter to these appointment type ids.
   *
   * Goes out as one `_id` search, so a caller holding a handful of bindings reads them in a single
   * request. Chunk above 100 ids — see `searchByIds`.
   */
  ids?: readonly string[];
  /**
   * Filter by provider availability. Only returns appointment types where this provider is in the actor array.
   */
  providerId?: string;
  /**
   * Filter by room assignment. Only returns appointment types where this room is in the actor array.
   */
  roomId?: string;
}

export interface PracticeAppointmentTypeSearchResult {
  appointmentTypes: PracticeAppointmentType[];
  total: number;
}

/** Location type for Cal.com event types. Used to gate Daily room creation. */
export type PracticeAppointmentTypeLocationType = "conferencing" | "inPerson" | "phone";

export interface CreatePracticeAppointmentTypeData {
  practiceId: string;
  practiceName?: string;
  calTeamEventTypeId: number;
  title: string;
  slug: string;
  color?: string;
  /** Patient-facing description, stored on the Schedule `comment` field. */
  description?: string;
  lengthInMinutes?: number;
  isPatientSelfScheduleEnabled?: boolean;
  type?: "provider" | "room";
  /** Location type for Cal.com (conferencing, inPerson, phone). Default inPerson. */
  locationType?: PracticeAppointmentTypeLocationType;
  /** Local mirror of Cal's per-day booking cap; omit/undefined for no limit. */
  maxBookingsPerDay?: number;
  providers?: Array<{ id: string; name?: string | null }>;
  rooms?: Array<{ id: string; name?: string | null }>;
  calChildEventTypes?: CalChildEventTypeMapping[];
  calRoundRobinEventType?: CalRoundRobinEventTypeMapping;
}

export interface UpdatePracticeAppointmentTypeData {
  title?: string;
  slug?: string;
  color?: string | null;
  /** Patient-facing description, stored on the Schedule `comment` field. `null` clears it. */
  description?: string | null;
  lengthInMinutes?: number;
  isPatientSelfScheduleEnabled?: boolean;
  surveys?: (Survey | string)[];
  documents?: (Document | string)[];
  /**
   * Hint charge items to attach to this appointment type.
   * When set, replaces the current Hint charge item associations.
   */
  hintChargeItems?: ({ id: string; name?: string } | string)[];
  /**
   * Providers to set as available for this appointment type.
   * Accepts objects with id and optional name.
   * When set, replaces the current provider selection in the actor array.
   */
  providers?: Array<{ id: string; name?: string | null }>;
  /**
   * Rooms to set as available for this appointment type.
   * Accepts objects with id and optional name.
   * When set, replaces the current room selection in the actor array.
   */
  rooms?: Array<{ id: string; name?: string | null }>;
  /**
   * The type of resource selection for this appointment.
   * - "provider": Uses clinician availability (can have multiple providers)
   * - "room": Uses treatment facility/room availability (single room)
   * When set, enforces mutual exclusivity between providers and rooms.
   */
  type?: "provider" | "room";
  /** Location type for Cal.com (conferencing, inPerson, phone). */
  locationType?: PracticeAppointmentTypeLocationType;
  /** Per-day booking cap mirror. `null` clears the limit; `undefined` leaves it unchanged. */
  maxBookingsPerDay?: number | null;
  calChildEventTypes?: CalChildEventTypeMapping[];
  calRoundRobinEventType?: CalRoundRobinEventTypeMapping | null;
  availabilitySchedule?: AvailabilityScheduleInput | null;
  hasExplicitAvailabilitySchedule?: boolean | null;
  derivedAvailabilitySchedules?: DerivedAvailabilityScheduleMapping[];
  /**
   * Stripe price ID to link to this appointment type. Pass `null` (or empty string) to unlink.
   * The id is validated against the practice's connected account in the server action. Mutually
   * exclusive with {@link hintPaymentChargeItemId} — `update()` itself clears the other
   * processor's link whenever this is set to a non-empty value, even if the caller didn't pass it.
   */
  stripePriceId?: string | null;
  /**
   * Hint charge item id to use as this appointment type's embed-booker payment basis. Pass `null`
   * (or empty string) to unlink. Mutually exclusive with {@link stripePriceId} — `update()` itself
   * clears the other processor's link whenever this is set to a non-empty value, even if the
   * caller didn't pass it. A type can only be linked to one payment processor at a time.
   */
  hintPaymentChargeItemId?: string | null;
  /**
   * Hours before the appointment that a Stripe "book now, invoice later" invoice comes due. Pass
   * `null` to reset to the default (see {@link PracticeAppointmentType.invoiceDueHoursBeforeAppointment}).
   */
  invoiceDueHoursBeforeAppointment?: number | null;
}

/**
 * PracticeAppointmentType represents practice-wide appointment types (Calendar V2)
 * Maps to FHIR Schedule resource with practice-level actors
 */
export class PracticeAppointmentType extends FhirResource<Schedule> {
  static readonly ResourceType = "Schedule";
  static readonly isSyncFrom = ["cal"];

  static readonly Identifier = {
    CalTeamEventType: CAL_IDENTIFIER_SYSTEM_TEAM_EVENT_TYPES,
    Practice: VIBRANT_IDENTIFIER_SYSTEM_PRACTICE_ID,
    ScheduleType: VIBRANT_IDENTIFIER_SYSTEM_SCHEDULE_TYPE,
  } as const;

  static readonly ScheduleTypeValue = "practice-appointment-type" as const;

  static readonly Extension = {
    Surveys: "vibrant.ProviderSchedule.surveys",
    Documents: "vibrant.schedule.documents",
    HintChargeItems: "vibrant.ProviderSchedule.hintChargeItems",
    Title: CAL_EXTENSION_URL_EVENT_TYPE_TITLE,
    Slug: CAL_EXTENSION_URL_EVENT_TYPE_SLUG,
    Color: "vibrant.schedule.color",
    Type: "vibrant.schedule.type",
    LocationType: "vibrant.schedule.location-type",
    PatientSelfScheduleEnabled: "vibrant.schedule.patient-self-schedule-enabled",
    LengthInMinutes: "vibrant.schedule.length-in-minutes",
    /** Local mirror of Cal's per-day booking cap (bookingLimitsCount.day) so the booking UI can enforce it. */
    MaxBookingsPerDay: "vibrant.schedule.max-bookings-per-day",
    CalChildEventTypes: "vibrant.schedule.cal-child-event-types",
    CalRoundRobinEventType: "vibrant.schedule.cal-round-robin-event-type",
    Availability: "vibrant.schedule.availability",
    ExplicitAvailabilitySaved: "vibrant.schedule.explicit-availability-saved",
    DerivedAvailabilitySchedules: "vibrant.schedule.derived-availability-schedules",
    // Stripe price (a.k.a. "service item") linked to this appointment type. When set
    // and the practice has a connected Stripe account, the embed booker collects payment
    // for this amount before confirming the booking. Configurable wherever the practice has
    // connected Stripe — no flag.
    StripePriceId: "vibrant.schedule.stripe-price-id",
    // Hint charge item ("service") linked to this appointment type as its embed-booker payment
    // basis. Mutually exclusive with StripePriceId — a type collects payment through exactly one
    // processor. When set and the practice is connected to Hint, the embed booker collects card/ACH
    // via Hint Payments (Rainforest/Stripe) before confirming the booking. Deliberately distinct
    // from HintChargeItems (which drives post-booking invoice billing, not an interactive checkout).
    // A Hint connection alone doesn't unlock this — it's gated by
    // `HINT_APPOINTMENT_TYPE_PAYMENTS_FLAG_KEY` (Stripe linking is ungated).
    HintPaymentChargeItemId: "vibrant.schedule.hint-payment-charge-item-id",
    // Hours before the appointment a Stripe "book now, invoice later" invoice comes due. Absent
    // means the default (see `invoiceDueHoursBeforeAppointment` getter) — Stripe-only; Hint's
    // invoicing has no equivalent due-date concept in this codebase.
    InvoiceDueHoursBeforeAppointment: "vibrant.schedule.invoice-due-hours-before-appointment",
  } as const;

  /** Location type for Cal.com event type (conferencing vs in-person vs phone). */
  static readonly LocationTypeValue = ["conferencing", "inPerson", "phone"] as const;
  /** Default location type when creating new records without explicit Cal.com location data. */
  static readonly DefaultLocationType = "inPerson" as const;

  static readonly Coding = {
    Documents: "vibrant.document-reference",
    HintChargeItems: "vibrant.hint.charge-items",
  } as const;

  get title(): string {
    return (
      findExtension(this.resource, CAL_EXTENSION_URL_EVENT_TYPE_TITLE)?.valueString ??
      "Untitled Appointment Type"
    );
  }

  get slug(): string {
    const slug = findExtension(this.resource, CAL_EXTENSION_URL_EVENT_TYPE_SLUG)?.valueString;
    if (!slug) {
      throw new Error(`PracticeAppointmentType ${this.id} has no slug`);
    }
    return slug;
  }

  get schedule(): Schedule {
    return this.resource;
  }

  /**
   * Returns the patient-facing appointment type description stored on the Schedule comment field.
   */
  get description(): string | undefined {
    const description = this.resource.comment?.trim();
    return description || undefined;
  }

  get calTeamEventTypeId(): number | undefined {
    const identifier = findIdentifier(this.resource, CAL_IDENTIFIER_SYSTEM_TEAM_EVENT_TYPES);
    if (!identifier?.value) return undefined;

    const value = parseInt(identifier.value, 10);
    if (Number.isNaN(value)) return undefined;
    return value;
  }

  /**
   * Get child event type mappings for MANAGED scheduling.
   */
  get calChildEventTypes(): CalChildEventTypeMapping[] {
    const raw = findExtension(
      this.resource,
      PracticeAppointmentType.Extension.CalChildEventTypes,
    )?.valueString;

    if (!raw) {
      return [];
    }

    try {
      const parsed = JSON.parse(raw);
      if (!Array.isArray(parsed)) {
        return [];
      }

      return parsed.filter((mapping) => {
        return typeof mapping?.calUserId === "number" && typeof mapping?.eventTypeId === "number";
      });
    } catch {
      return [];
    }
  }

  /**
   * Get the child event type ID for a specific Cal.com user ID.
   */
  getChildEventTypeIdForCalUser(calUserId?: number | null): number | undefined {
    if (calUserId == null) {
      return undefined;
    }

    return this.calChildEventTypes.find((mapping) => mapping.calUserId === calUserId)?.eventTypeId;
  }

  /**
   * Cal team round-robin event used for pooled patient booking.
   */
  get calRoundRobinEventType(): CalRoundRobinEventTypeMapping | undefined {
    const raw = findExtension(
      this.resource,
      PracticeAppointmentType.Extension.CalRoundRobinEventType,
    )?.valueString;

    if (!raw) {
      return undefined;
    }

    try {
      const parsed = JSON.parse(raw);
      if (typeof parsed?.eventTypeId !== "number" || typeof parsed?.slug !== "string") {
        return undefined;
      }

      return {
        eventTypeId: parsed.eventTypeId,
        slug: parsed.slug,
        providerIdsFingerprint:
          typeof parsed.providerIdsFingerprint === "string"
            ? parsed.providerIdsFingerprint
            : undefined,
      };
    } catch {
      return undefined;
    }
  }

  /**
   * Returns the last saved appointment-type availability persisted by Vibrant.
   */
  get availabilityScheduleInput(): AvailabilityScheduleInput | undefined {
    const raw = findExtension(
      this.resource,
      PracticeAppointmentType.Extension.Availability,
    )?.valueString;
    if (!raw) {
      return undefined;
    }

    try {
      const parsed = JSON.parse(raw);
      const result = AvailabilityScheduleInputSchema.safeParse(parsed);
      return result.success ? result.data : undefined;
    } catch {
      return undefined;
    }
  }

  /**
   * Returns the last saved appointment-type availability persisted by Vibrant.
   */
  get availabilityFormValues(): AvailabilityFormValues | undefined {
    return this.availabilityScheduleInput
      ? buildAvailabilityFormValuesFromScheduleInput({
          scheduleInput: this.availabilityScheduleInput,
        })
      : undefined;
  }

  /**
   * True only after availability was explicitly saved through our UI flow.
   */
  get hasExplicitAvailabilitySchedule(): boolean {
    return (
      findExtension(this.resource, PracticeAppointmentType.Extension.ExplicitAvailabilitySaved)
        ?.valueBoolean ?? false
    );
  }

  /**
   * Returns the last derived child schedule metadata keyed by Cal user.
   */
  get derivedAvailabilitySchedules(): DerivedAvailabilityScheduleMapping[] {
    const raw = findExtension(
      this.resource,
      PracticeAppointmentType.Extension.DerivedAvailabilitySchedules,
    )?.valueString;
    if (!raw) {
      return [];
    }

    try {
      const parsed = JSON.parse(raw);
      if (!Array.isArray(parsed)) {
        return [];
      }

      return parsed.filter((mapping) => {
        return (
          typeof mapping?.calUserId === "number" &&
          typeof mapping?.scheduleId === "number" &&
          (mapping?.fingerprint === undefined || typeof mapping?.fingerprint === "string")
        );
      });
    } catch {
      return [];
    }
  }

  /**
   * Returns the derived child schedule metadata for a specific Cal user.
   */
  getDerivedAvailabilityScheduleForCalUser(
    calUserId?: number | null,
  ): DerivedAvailabilityScheduleMapping | undefined {
    if (calUserId == null) {
      return undefined;
    }

    return this.derivedAvailabilitySchedules.find((mapping) => mapping.calUserId === calUserId);
  }

  get practiceId(): string | undefined {
    const identifier = findIdentifier(this.resource, VIBRANT_IDENTIFIER_SYSTEM_PRACTICE_ID);
    return identifier?.value;
  }

  /**
   * Get the color for this appointment type
   */
  get color(): string | undefined {
    const extension = findExtension(this.resource, PracticeAppointmentType.Extension.Color);
    return extension?.valueString;
  }

  /**
   * Get the type of appointment (provider or room)
   */
  get type(): "provider" | "room" | undefined {
    const extension = findExtension(this.resource, PracticeAppointmentType.Extension.Type);
    return extension?.valueCode as "provider" | "room" | undefined;
  }

  /** True only when the locationType extension is explicitly stored on this record.
   * False for records created before the extension was introduced — callers that
   * need the real type should fall back to the Cal.com API when this is false. */
  get hasExplicitLocationType(): boolean {
    return !!findExtension(this.resource, PracticeAppointmentType.Extension.LocationType);
  }

  /** Explicitly stored location type on the schedule, if present. */
  get explicitLocationType(): PracticeAppointmentTypeLocationType | undefined {
    const extension = findExtension(this.resource, PracticeAppointmentType.Extension.LocationType);
    const value = extension?.valueCode as PracticeAppointmentTypeLocationType | undefined;
    if (
      value &&
      PracticeAppointmentType.LocationTypeValue.includes(
        value as PracticeAppointmentTypeLocationType,
      )
    ) {
      return value;
    }

    return undefined;
  }

  /** Location type (conferencing, inPerson, phone).
   * Legacy schedules without the extension still default to conferencing for
   * compatibility, but callers should prefer `explicitLocationType` when they
   * need to know whether modality is actually persisted. */
  get locationType(): PracticeAppointmentTypeLocationType {
    if (this.explicitLocationType) {
      return this.explicitLocationType;
    }

    // Backward compat: pre-extension records were all conferencing (video)
    return "conferencing";
  }

  /**
   * Check if patient self-scheduling is enabled
   */
  get isPatientSelfScheduleEnabled(): boolean {
    const extension = findExtension(
      this.resource,
      PracticeAppointmentType.Extension.PatientSelfScheduleEnabled,
    );
    return extension?.valueBoolean ?? false;
  }

  /**
   * Get the duration/length of the appointment in minutes
   */
  get lengthInMinutes(): number | undefined {
    const extension = findExtension(
      this.resource,
      PracticeAppointmentType.Extension.LengthInMinutes,
    );
    return extension?.valueInteger;
  }

  /**
   * Maximum number of times this appointment type can be booked per day (Cal's `bookingLimitsCount.day`),
   * mirrored locally so the booking modal can enforce it without a Cal round-trip. Undefined = no limit.
   */
  get maxBookingsPerDay(): number | undefined {
    const extension = findExtension(
      this.resource,
      PracticeAppointmentType.Extension.MaxBookingsPerDay,
    );
    return typeof extension?.valueInteger === "number" && extension.valueInteger > 0
      ? extension.valueInteger
      : undefined;
  }

  /**
   * Stripe price ID ("service item") linked to this appointment type, if any.
   * Lives on the practice's connected Stripe account; resolve its amount/currency
   * from Stripe at request time rather than caching it here.
   */
  get stripePriceId(): string | undefined {
    const extension = findExtension(this.resource, PracticeAppointmentType.Extension.StripePriceId);
    return extension?.valueString?.trim() || undefined;
  }

  /**
   * Hint charge item id used as this appointment type's embed-booker payment basis, if any.
   * Mutually exclusive with {@link stripePriceId}. Resolve amount/currency/name from Hint at
   * request time rather than caching them here (mirrors the Stripe price pattern).
   */
  get hintPaymentChargeItemId(): string | undefined {
    const extension = findExtension(
      this.resource,
      PracticeAppointmentType.Extension.HintPaymentChargeItemId,
    );
    return extension?.valueString?.trim() || undefined;
  }

  /**
   * Hours before the appointment a Stripe "book now, invoice later" invoice comes due, if
   * customized. `undefined` means the caller should fall back to its own default (48 hours —
   * see `INVOICE_DUE_BEFORE_APPOINTMENT_HOURS` in `lib/stripe/invoices.ts`). Stripe-only.
   */
  get invoiceDueHoursBeforeAppointment(): number | undefined {
    const extension = findExtension(
      this.resource,
      PracticeAppointmentType.Extension.InvoiceDueHoursBeforeAppointment,
    );
    return typeof extension?.valueInteger === "number" && extension.valueInteger > 0
      ? extension.valueInteger
      : undefined;
  }

  /** Which processor this type charges through, if a product is linked. */
  get paymentProcessor(): "stripe" | "hint" | undefined {
    if (this.stripePriceId) return "stripe";
    if (this.hintPaymentChargeItemId) return "hint";
    return undefined;
  }

  /**
   * True when the embed booker must hold this type's bookings pending until payment — i.e. a
   * Stripe price OR a Hint charge item is linked. Drives the Cal `requiresConfirmation` gate. How a
   * booking is actually charged (collect up front vs. invoice) is determined by the booking surface
   * at booking time (widget always collects, the app always invoices), not stored on the type.
   */
  get requiresEmbedPaymentHold(): boolean {
    return Boolean(this.paymentProcessor);
  }

  /**
   * True when this appointment type books a treatment room (Location) rather than a provider.
   * Room-based types have no bookable providers — the embed booker skips provider selection and
   * books against the room's own Cal schedule. Falls back to actor shape for legacy records that
   * predate the explicit `type` extension.
   */
  get isRoomBased(): boolean {
    if (this.type === "room") {
      return true;
    }
    return this.providerIds.length === 0 && this.roomIds.length > 0;
  }

  /**
   * Get the questionnaire references associated with this appointment type
   */
  get questionnaireReferences(): Reference<Questionnaire>[] {
    const surveyExtension = this.resource.extension?.find(
      (ext) => ext.url === PracticeAppointmentType.Extension.Surveys,
    );

    if (!surveyExtension?.valueCodeableConcept?.coding) {
      return [];
    }

    return surveyExtension.valueCodeableConcept.coding
      .map((coding) => {
        if (
          !coding.code?.startsWith("Questionnaire/") ||
          coding.system !== "vibrant.survey-reference"
        ) {
          return undefined;
        }

        const parts = coding.code.split("/");
        if (parts.length !== 2) {
          return undefined;
        }

        const resourceId = parts[1].trim();
        if (!resourceId) {
          return undefined;
        }

        return {
          reference: coding.code,
        } as Reference<Questionnaire>;
      })
      .filter((ref): ref is Reference<Questionnaire> => ref !== undefined);
  }

  /**
   * Get the document references associated with this appointment type.
   * These are stored as CodeableConcept codings with the DocumentReference ref as the code.
   */
  get documentReferences(): Reference<DocumentReference>[] {
    const documentsExtension = this.resource.extension?.find(
      (ext) => ext.url === PracticeAppointmentType.Extension.Documents,
    );

    if (!documentsExtension?.valueCodeableConcept?.coding) {
      return [];
    }

    return documentsExtension.valueCodeableConcept.coding
      .map((coding) => {
        if (
          !coding.code?.startsWith("DocumentReference/") ||
          coding.system !== PracticeAppointmentType.Coding.Documents
        ) {
          return undefined;
        }

        const parts = coding.code.split("/");
        if (parts.length !== 2) {
          return undefined;
        }

        const resourceId = parts[1].trim();
        if (!resourceId) {
          return undefined;
        }

        return {
          reference: coding.code,
          display: coding.display?.trim() || undefined,
        } as Reference<DocumentReference>;
      })
      .filter((ref): ref is Reference<DocumentReference> => ref !== undefined);
  }

  /**
   * Get the Hint charge item references associated with this appointment type.
   * These are stored as CodeableConcept codings with the Hint charge item ID as the code.
   */
  get hintChargeItemReferences(): HintChargeItemReference[] {
    const hintChargeExtension = this.resource.extension?.find(
      (ext) => ext.url === PracticeAppointmentType.Extension.HintChargeItems,
    );

    if (!hintChargeExtension?.valueCodeableConcept?.coding) {
      return [];
    }

    return hintChargeExtension.valueCodeableConcept.coding
      .map((coding) => {
        if (coding.system !== PracticeAppointmentType.Coding.HintChargeItems || !coding.code) {
          return undefined;
        }

        const trimmedDisplay = coding.display?.trim();

        if (trimmedDisplay) {
          return {
            id: coding.code,
            name: trimmedDisplay,
          };
        }

        return {
          id: coding.code,
        };
      })
      .filter((reference): reference is HintChargeItemReference => reference !== undefined);
  }

  /**
   * Get all actor references for this appointment type
   * For practice-wide appointment types, this will only include the Organization
   */
  get actors(): Reference[] {
    return this.resource.actor || [];
  }

  /**
   * Type guard that checks if a reference matches the expected resource type prefix.
   * Returns true if the reference starts with "ResourceType/".
   */
  private isActorOfType<T extends Resource>(
    actor: Reference,
    resourceType: string,
  ): actor is Reference<T> {
    return actor.reference?.startsWith(`${resourceType}/`) ?? false;
  }

  /**
   * Filters actors by resource type and returns typed references.
   */
  private getActorRefs<T extends Resource>(resourceType: string): Reference<T>[] {
    return this.actors.filter((actor): actor is Reference<T> =>
      this.isActorOfType<T>(actor, resourceType),
    );
  }

  /**
   * Extracts IDs from actor references matching the given resource type.
   */
  private getActorIds(resourceType: string): string[] {
    const prefix = `${resourceType}/`;
    return this.getActorRefs(resourceType)
      .map((ref) => ref.reference?.replace(prefix, ""))
      .filter((id): id is string => id !== undefined);
  }

  /**
   * Get the provider (Practitioner) references from the actor array.
   * These are the providers that can use this appointment type.
   */
  get providerReferences(): Reference<Practitioner>[] {
    return this.getActorRefs<Practitioner>("Practitioner");
  }

  /**
   * Get the provider IDs from the actor array.
   */
  get providerIds(): string[] {
    return this.getActorIds("Practitioner");
  }

  /**
   * Check if a specific provider can use this appointment type.
   */
  hasProvider(providerId: string): boolean {
    return this.providerIds.includes(providerId);
  }

  /**
   * Get the room (Location) references from the actor array.
   * These are the rooms that can be used with this appointment type.
   */
  get roomReferences(): Reference<Location>[] {
    return this.getActorRefs<Location>("Location");
  }

  /**
   * Get the room IDs from the actor array.
   */
  get roomIds(): string[] {
    return this.getActorIds("Location");
  }

  /**
   * Check if a specific room can be used with this appointment type.
   */
  hasRoom(roomId: string): boolean {
    return this.roomIds.includes(roomId);
  }

  async calEventType(cal: CalClient): Promise<CalEventType | undefined> {
    if (!this.calTeamEventTypeId) return undefined;
    return cal.getEventType(this.calTeamEventTypeId);
  }

  /**
   * Get all surveys associated with this appointment type
   */
  async getSurveys(medplum: MedplumClient): Promise<Survey[]> {
    const surveyRefs = this.questionnaireReferences;
    if (surveyRefs.length === 0) return [];

    const surveys = await Promise.all(
      surveyRefs.map(async (ref) => {
        try {
          return await Survey.getByReference(medplum, ref);
        } catch {
          logger.warn(`Failed to load survey ${ref.reference}`);
          return undefined;
        }
      }),
    );

    return surveys.filter((survey): survey is Survey => survey !== undefined);
  }

  /**
   * Set the surveys associated with this appointment type.
   * Convenience method that delegates to the static update method.
   */
  async setSurveys(
    medplum: MedplumClient,
    surveys: (Survey | string)[],
  ): Promise<PracticeAppointmentType> {
    return PracticeAppointmentType.update(medplum, this.id, { surveys });
  }

  /**
   * Get all documents associated with this appointment type.
   */
  async getDocuments(medplum: MedplumClient): Promise<Document[]> {
    const documentRefs = this.documentReferences;
    if (documentRefs.length === 0) return [];

    const documents = await Promise.all(
      documentRefs.map(async (ref) => {
        try {
          if (!ref.reference) {
            return undefined;
          }

          return await Document.getByReference(medplum, ref.reference);
        } catch {
          logger.warn(`Failed to load document ${ref.reference}`);
          return undefined;
        }
      }),
    );

    return documents.filter((document): document is Document => document !== undefined);
  }

  /**
   * Set the documents associated with this appointment type.
   * Convenience method that delegates to the static update method.
   */
  async setDocuments(
    medplum: MedplumClient,
    documents: (Document | string)[],
  ): Promise<PracticeAppointmentType> {
    return PracticeAppointmentType.update(medplum, this.id, { documents });
  }

  /**
   * Set the Hint charge items associated with this appointment type.
   * This replaces any existing Hint charge item associations.
   *
   * @param medplum - The Medplum client.
   * @param chargeItems - Array of Hint charge item identifiers or objects with id/name metadata.
   * @returns Updated PracticeAppointmentType instance.
   */
  async setHintChargeItems(
    medplum: MedplumClient,
    chargeItems: ({ id: string; name?: string } | string)[],
  ): Promise<PracticeAppointmentType> {
    const deduplicated = deduplicateHintChargeItemReferences({
      chargeItems,
      missingIdErrorMessage: "All Hint charge items must have an id",
    });

    // Never let this type's own payment-gate item also live in the "extra charge items" list —
    // `sendBookingInvoicesForAppointment` already bills that item, so keeping it here would
    // double-charge every booking of this type. A genuinely distinct add-on item is unaffected.
    const paymentGateItemId = this.hintPaymentChargeItemId;
    const uniqueChargeItems = deduplicated.filter((item) => {
      if (item.id !== paymentGateItemId) {
        return true;
      }
      logger.warn(
        "Dropping charge item from setHintChargeItems: it's this type's payment-gate item",
        {
          scheduleId: this.id,
          chargeItemId: item.id,
        },
      );
      return false;
    });

    // Get current extensions excluding Hint charge item extensions
    const otherExtensions =
      this.resource.extension?.filter(
        (ext) => ext.url !== PracticeAppointmentType.Extension.HintChargeItems,
      ) ?? [];

    // Create a single Hint charge item extension with all references in the coding array
    const hintChargeItemExtension =
      uniqueChargeItems.length > 0
        ? [
            {
              url: PracticeAppointmentType.Extension.HintChargeItems,
              valueCodeableConcept: {
                coding: uniqueChargeItems.map((item) => ({
                  code: item.id,
                  system: PracticeAppointmentType.Coding.HintChargeItems,
                  display: item.name,
                })),
              },
            },
          ]
        : [
            {
              url: PracticeAppointmentType.Extension.HintChargeItems,
              valueCodeableConcept: null,
            },
          ];

    // Combine all extensions
    const updatedExtensions = [...otherExtensions, ...hintChargeItemExtension];

    const updatedSchedule = await PracticeAppointmentType.patchResource(medplum, {
      id: this.id,
      patch: {
        extension: updatedExtensions,
      },
    });

    return new PracticeAppointmentType(updatedSchedule);
  }

  static async getById(medplum: MedplumClient, id: string): Promise<PracticeAppointmentType> {
    const schedule = await medplum.readResource("Schedule", id);
    return new PracticeAppointmentType(schedule);
  }

  /**
   * Creates a new practice appointment type.
   *
   * @param medplum - The Medplum client
   * @param data - The data for the appointment type
   * @returns The created appointment type
   */
  static async create(
    medplum: MedplumClient,
    data: CreatePracticeAppointmentTypeData,
  ): Promise<PracticeAppointmentType> {
    if (!data.practiceId) throw new Error("practiceId is required");
    if (!data.calTeamEventTypeId) throw new Error("calTeamEventTypeId is required");
    if (!data.title) throw new Error("title is required");
    if (!data.slug) throw new Error("slug is required");

    const schedule = await PracticeAppointmentType.createResource(
      medplum,
      PracticeAppointmentType.toFhirObject(data),
    );
    return new PracticeAppointmentType(schedule);
  }

  /**
   * Updates a practice appointment type with the provided data.
   *
   * @param medplum - The Medplum client.
   * @param id - The ID of the appointment type to update.
   * @param data - The data to update.
   * @returns The updated appointment type.
   */
  static async update(
    medplum: MedplumClient,
    id: string,
    data: UpdatePracticeAppointmentTypeData,
  ): Promise<PracticeAppointmentType> {
    const existing = await PracticeAppointmentType.getById(medplum, id);

    // Build extensions array, preserving existing extensions not being updated
    const extensionUrlsToUpdate = new Set<string>();
    const newExtensions: PracticeAppointmentTypeExtension[] = [];

    // Handle title
    if (data.title !== undefined) {
      extensionUrlsToUpdate.add(PracticeAppointmentType.Extension.Title);
      newExtensions.push({
        url: PracticeAppointmentType.Extension.Title,
        valueString: data.title,
      });
    }

    // Handle slug
    if (data.slug !== undefined) {
      extensionUrlsToUpdate.add(PracticeAppointmentType.Extension.Slug);
      newExtensions.push({
        url: PracticeAppointmentType.Extension.Slug,
        valueString: data.slug,
      });
    }

    // Handle lengthInMinutes
    if (data.lengthInMinutes !== undefined) {
      extensionUrlsToUpdate.add(PracticeAppointmentType.Extension.LengthInMinutes);
      newExtensions.push({
        url: PracticeAppointmentType.Extension.LengthInMinutes,
        valueInteger: data.lengthInMinutes,
      });
    }

    // Handle color coding
    if (data.color !== undefined) {
      extensionUrlsToUpdate.add(PracticeAppointmentType.Extension.Color);

      if (typeof data.color === "string" && data.color.length > 0) {
        newExtensions.push({
          url: PracticeAppointmentType.Extension.Color,
          valueString: data.color,
        });
      } else {
        // `patchResource`'s extension merge is URL-keyed, not a full-array replace — omitting an
        // extension from the patch leaves the server's existing value untouched. An explicit
        // null-valued entry is what actually removes it (mirrors the Availability handling below).
        newExtensions.push({ url: PracticeAppointmentType.Extension.Color, valueString: null });
      }
    }

    // Handle Cal child event type mappings
    if (data.calChildEventTypes !== undefined) {
      extensionUrlsToUpdate.add(PracticeAppointmentType.Extension.CalChildEventTypes);

      if (data.calChildEventTypes.length > 0) {
        newExtensions.push({
          url: PracticeAppointmentType.Extension.CalChildEventTypes,
          valueString: JSON.stringify(data.calChildEventTypes),
        });
      } else {
        newExtensions.push({
          url: PracticeAppointmentType.Extension.CalChildEventTypes,
          valueString: null,
        });
      }
    }

    if (data.calRoundRobinEventType !== undefined) {
      extensionUrlsToUpdate.add(PracticeAppointmentType.Extension.CalRoundRobinEventType);

      if (data.calRoundRobinEventType !== null) {
        newExtensions.push({
          url: PracticeAppointmentType.Extension.CalRoundRobinEventType,
          valueString: JSON.stringify(data.calRoundRobinEventType),
        });
      } else {
        newExtensions.push({
          url: PracticeAppointmentType.Extension.CalRoundRobinEventType,
          valueString: null,
        });
      }
    }

    // Persist the last saved appointment-type availability so background sync does not
    // depend on re-reading the parent Cal schedule through host credentials.
    if (data.availabilitySchedule !== undefined) {
      extensionUrlsToUpdate.add(PracticeAppointmentType.Extension.Availability);
      if (data.availabilitySchedule === null) {
        newExtensions.push({
          url: PracticeAppointmentType.Extension.Availability,
          valueString: null,
        });
      } else {
        newExtensions.push({
          url: PracticeAppointmentType.Extension.Availability,
          valueString: JSON.stringify(data.availabilitySchedule),
        });
      }
    }

    if (data.hasExplicitAvailabilitySchedule !== undefined) {
      extensionUrlsToUpdate.add(PracticeAppointmentType.Extension.ExplicitAvailabilitySaved);
      if (data.hasExplicitAvailabilitySchedule !== null) {
        newExtensions.push({
          url: PracticeAppointmentType.Extension.ExplicitAvailabilitySaved,
          valueBoolean: data.hasExplicitAvailabilitySchedule,
        });
      } else {
        newExtensions.push({
          url: PracticeAppointmentType.Extension.ExplicitAvailabilitySaved,
          valueBoolean: null,
        });
      }
    }

    if (data.derivedAvailabilitySchedules !== undefined) {
      extensionUrlsToUpdate.add(PracticeAppointmentType.Extension.DerivedAvailabilitySchedules);
      if (data.derivedAvailabilitySchedules.length > 0) {
        newExtensions.push({
          url: PracticeAppointmentType.Extension.DerivedAvailabilitySchedules,
          valueString: JSON.stringify(data.derivedAvailabilitySchedules),
        });
      } else {
        newExtensions.push({
          url: PracticeAppointmentType.Extension.DerivedAvailabilitySchedules,
          valueString: null,
        });
      }
    }

    // Handle isPatientSelfScheduleEnabled
    if (data.isPatientSelfScheduleEnabled !== undefined) {
      extensionUrlsToUpdate.add(PracticeAppointmentType.Extension.PatientSelfScheduleEnabled);
      newExtensions.push({
        url: PracticeAppointmentType.Extension.PatientSelfScheduleEnabled,
        valueBoolean: data.isPatientSelfScheduleEnabled,
      });
    }

    // Handle surveys
    if (data.surveys !== undefined) {
      extensionUrlsToUpdate.add(PracticeAppointmentType.Extension.Surveys);

      const uniqueSurveyIds = [
        ...new Set(
          data.surveys.map((survey) => {
            const surveyId = typeof survey === "string" ? survey : survey.id;
            if (!surveyId) {
              throw new Error("All surveys must have a persistent id");
            }
            return surveyId;
          }),
        ),
      ];

      if (uniqueSurveyIds.length > 0) {
        newExtensions.push({
          url: PracticeAppointmentType.Extension.Surveys,
          valueCodeableConcept: {
            coding: uniqueSurveyIds.map((surveyId) => ({
              code: `Questionnaire/${surveyId}`,
              system: "vibrant.survey-reference",
            })),
          },
        });
      } else {
        newExtensions.push({
          url: PracticeAppointmentType.Extension.Surveys,
          valueCodeableConcept: null,
        });
      }
    }

    // Handle documents
    if (data.documents !== undefined) {
      extensionUrlsToUpdate.add(PracticeAppointmentType.Extension.Documents);

      const uniqueDocuments = Array.from(
        data.documents
          .reduce((documentMap, document) => {
            const documentId = typeof document === "string" ? document : document.id;
            if (!documentId) {
              throw new Error("All documents must have a persistent id");
            }

            const display =
              typeof document === "string"
                ? undefined
                : document.description?.trim() || document.fileName;
            const existingDocument = documentMap.get(documentId);

            if (!existingDocument || (!existingDocument.display && display)) {
              documentMap.set(documentId, { id: documentId, display });
            }

            return documentMap;
          }, new Map<string, { id: string; display?: string }>())
          .values(),
      );

      if (uniqueDocuments.length > 0) {
        newExtensions.push({
          url: PracticeAppointmentType.Extension.Documents,
          valueCodeableConcept: {
            coding: uniqueDocuments.map((document) => ({
              code: `DocumentReference/${document.id}`,
              system: PracticeAppointmentType.Coding.Documents,
              display: document.display,
            })),
          },
        });
      } else {
        newExtensions.push({
          url: PracticeAppointmentType.Extension.Documents,
          valueCodeableConcept: null,
        });
      }
    }

    // Handle hintChargeItems
    if (data.hintChargeItems !== undefined) {
      extensionUrlsToUpdate.add(PracticeAppointmentType.Extension.HintChargeItems);

      const uniqueChargeItems = deduplicateHintChargeItemReferences({
        chargeItems: data.hintChargeItems,
        missingIdErrorMessage: "All Hint charge items must have an id",
      });

      if (uniqueChargeItems.length > 0) {
        newExtensions.push({
          url: PracticeAppointmentType.Extension.HintChargeItems,
          valueCodeableConcept: {
            coding: uniqueChargeItems.map((item) => ({
              code: item.id,
              system: PracticeAppointmentType.Coding.HintChargeItems,
              display: item.name,
            })),
          },
        });
      } else {
        newExtensions.push({
          url: PracticeAppointmentType.Extension.HintChargeItems,
          valueCodeableConcept: null,
        });
      }
    }

    // Handle per-day booking cap mirror (null clears it; a positive number sets it)
    if (data.maxBookingsPerDay !== undefined) {
      extensionUrlsToUpdate.add(PracticeAppointmentType.Extension.MaxBookingsPerDay);
      if (typeof data.maxBookingsPerDay === "number" && data.maxBookingsPerDay > 0) {
        newExtensions.push({
          url: PracticeAppointmentType.Extension.MaxBookingsPerDay,
          valueInteger: data.maxBookingsPerDay,
        });
      } else {
        newExtensions.push({
          url: PracticeAppointmentType.Extension.MaxBookingsPerDay,
          valueInteger: null,
        });
      }
    }

    // Handle invoice due-date offset (null resets to the default; a positive number sets it)
    if (data.invoiceDueHoursBeforeAppointment !== undefined) {
      extensionUrlsToUpdate.add(PracticeAppointmentType.Extension.InvoiceDueHoursBeforeAppointment);
      if (
        typeof data.invoiceDueHoursBeforeAppointment === "number" &&
        data.invoiceDueHoursBeforeAppointment > 0
      ) {
        newExtensions.push({
          url: PracticeAppointmentType.Extension.InvoiceDueHoursBeforeAppointment,
          valueInteger: data.invoiceDueHoursBeforeAppointment,
        });
      } else {
        newExtensions.push({
          url: PracticeAppointmentType.Extension.InvoiceDueHoursBeforeAppointment,
          valueInteger: null,
        });
      }
    }

    // A type can only be linked to one payment processor at a time. `update()` enforces this
    // itself — linking one always clears the other's extension, even if the caller only passed
    // one field — so no caller can accidentally leave both a Stripe price and a Hint charge item
    // linked simultaneously.
    const trimmedStripePriceId = data.stripePriceId?.trim();
    const trimmedHintChargeItemId = data.hintPaymentChargeItemId?.trim();
    const isLinkingStripePrice = Boolean(trimmedStripePriceId);
    const isLinkingHintChargeItem = Boolean(trimmedHintChargeItemId);
    if (isLinkingStripePrice && isLinkingHintChargeItem) {
      throw new Error(
        "PracticeAppointmentType.update: cannot link both a Stripe price and a Hint charge item — a type can only be linked to one payment processor",
      );
    }

    // Handle Stripe price link. Empty/null clears the link — must push an explicit null-valued
    // entry (not just omit it): `patchResource`'s extension merge is URL-keyed, so an omitted url
    // leaves the server's current value untouched rather than removing it.
    if (data.stripePriceId !== undefined || isLinkingHintChargeItem) {
      extensionUrlsToUpdate.add(PracticeAppointmentType.Extension.StripePriceId);
      if (trimmedStripePriceId) {
        newExtensions.push({
          url: PracticeAppointmentType.Extension.StripePriceId,
          valueString: trimmedStripePriceId,
        });
      } else {
        newExtensions.push({
          url: PracticeAppointmentType.Extension.StripePriceId,
          valueString: null,
        });
      }
    }

    // Handle Hint payment charge item link. Empty/null clears the link — same explicit-null rule.
    if (data.hintPaymentChargeItemId !== undefined || isLinkingStripePrice) {
      extensionUrlsToUpdate.add(PracticeAppointmentType.Extension.HintPaymentChargeItemId);
      if (trimmedHintChargeItemId) {
        newExtensions.push({
          url: PracticeAppointmentType.Extension.HintPaymentChargeItemId,
          valueString: trimmedHintChargeItemId,
        });
      } else {
        newExtensions.push({
          url: PracticeAppointmentType.Extension.HintPaymentChargeItemId,
          valueString: null,
        });
      }
    }

    // Handle locationType (conferencing, inPerson, phone)
    if (data.locationType !== undefined) {
      extensionUrlsToUpdate.add(PracticeAppointmentType.Extension.LocationType);
      newExtensions.push({
        url: PracticeAppointmentType.Extension.LocationType,
        valueCode: data.locationType,
      });
    }

    // Handle type (provider vs room)
    // Use local variables to avoid mutating the input data object
    let providersToSet = data.providers;
    let roomsToSet = data.rooms;

    if (data.type !== undefined) {
      extensionUrlsToUpdate.add(PracticeAppointmentType.Extension.Type);
      newExtensions.push({
        url: PracticeAppointmentType.Extension.Type,
        valueCode: data.type,
      });

      // Enforce mutual exclusivity: when type is set, clear the opposite actors
      if (data.type === "room") {
        // Clear providers when type is room
        providersToSet = [];
      } else if (data.type === "provider") {
        // Clear rooms when type is provider
        roomsToSet = [];
      }
    }

    // Filter out extensions that are being updated, keep the rest
    const preservedExtensions =
      existing.resource.extension?.filter((ext) => !extensionUrlsToUpdate.has(ext.url)) ?? [];

    const updatedExtensions = [...preservedExtensions, ...newExtensions];

    // Build the patch object
    const patch: PracticeAppointmentTypePatch = {
      extension: updatedExtensions as Extension[],
    };

    // Description lives on the plain Schedule.comment field, not an extension. `patchResource`
    // skips an `undefined` value entirely (leaves the existing field untouched) and only clears on
    // `null`/`""` - so clearing must send `null`, not `undefined`.
    if (data.description !== undefined) {
      patch.comment = data.description?.trim() || null;
    }

    // Handle providers and rooms - update the actor array
    // Also triggered when type is set (due to mutual exclusivity enforcement above)
    if (providersToSet !== undefined || roomsToSet !== undefined || data.type !== undefined) {
      // Keep Organization references
      const organizationActors: Reference<Organization>[] = existing.actors.filter(
        (actor): actor is Reference<Organization> =>
          actor.reference?.startsWith("Organization/") ?? false,
      );

      // Determine Practitioner references (use new if provided, otherwise preserve existing)
      const providerActors: Reference<Practitioner>[] =
        providersToSet !== undefined
          ? providersToSet.map((provider) => ({
              reference: `Practitioner/${provider.id}`,
              display: provider.name ?? undefined,
            }))
          : existing.providerReferences;

      // Determine Location (Room) references (use new if provided, otherwise preserve existing)
      const roomActors: Reference<Location>[] =
        roomsToSet !== undefined
          ? roomsToSet.map((room) => ({
              reference: `Location/${room.id}`,
              display: room.name ?? undefined,
            }))
          : existing.roomReferences;

      patch.actor = [...organizationActors, ...providerActors, ...roomActors];
    }

    const schedule = await PracticeAppointmentType.patchResource(medplum, { id, patch });
    return new PracticeAppointmentType(schedule);
  }

  /**
   * Updates a practice appointment type from a Cal.com event type.
   * Convenience method for syncing Cal.com event type changes to FHIR.
   *
   * @param medplum - The Medplum client.
   * @param id - The ID of the appointment type to update.
   * @param eventType - The Cal.com event type data.
   * @returns The updated appointment type.
   */
  static async updateFromEventType(
    medplum: MedplumClient,
    id: string,
    eventType: CalEventType | FormValues,
  ): Promise<PracticeAppointmentType> {
    const updateData: UpdatePracticeAppointmentTypeData = {
      title: eventType.title as string,
      slug: eventType.slug as string,
    };

    if ("description" in eventType && typeof eventType.description === "string") {
      updateData.description = eventType.description;
    }

    // Cal.com API uses `lengthInMinutes`, but FormValues (from atoms) uses `length`
    if ("lengthInMinutes" in eventType && typeof eventType.lengthInMinutes === "number") {
      updateData.lengthInMinutes = eventType.lengthInMinutes;
    } else if ("length" in eventType && typeof eventType.length === "number") {
      updateData.lengthInMinutes = eventType.length;
    }

    // Sync only recognized Cal.com location types; unknown vendor tokens should not mutate modality.
    const locations = (eventType as { locations?: Array<{ type?: string }> }).locations;
    if (Array.isArray(locations) && locations.length > 0 && locations[0]?.type) {
      const locationType = maybeLocationTypeFromCalLocation(locations[0].type);
      if (locationType) {
        updateData.locationType = locationType;
      }
    }

    // Mirror Cal's per-day booking cap (`bookingLimitsCount.day`) locally. Present-but-disabled or
    // present-without-a-day clears the mirror; absent (e.g. atoms FormValues) leaves it unchanged.
    const bookingLimitsCount = (eventType as { bookingLimitsCount?: unknown }).bookingLimitsCount;
    if (bookingLimitsCount !== undefined) {
      const isActiveDayLimit =
        typeof bookingLimitsCount === "object" &&
        bookingLimitsCount !== null &&
        !("disabled" in bookingLimitsCount) &&
        typeof (bookingLimitsCount as { day?: unknown }).day === "number";
      updateData.maxBookingsPerDay = isActiveDayLimit
        ? (bookingLimitsCount as { day: number }).day
        : null;
    }

    return PracticeAppointmentType.update(medplum, id, updateData);
  }

  /**
   * Deletes a practice appointment type and its associated Cal.com team event type
   */
  async deleteAppointmentType(medplum: MedplumClient, cal: CalClient): Promise<void> {
    if (this.calTeamEventTypeId) {
      try {
        await cal.deleteEventType(this.calTeamEventTypeId);
        logger.info(`Deleted Cal.com team event type ${this.calTeamEventTypeId}`);
      } catch (error) {
        logger.warn(`Failed to delete Cal.com team event type ${this.calTeamEventTypeId}:`, error);
      }
    }

    await PracticeAppointmentType.delete(medplum, this.id);
    logger.info(`Deleted practice appointment type ${this.id}`);
  }

  /**
   * Search all appointment types matching the given query parameters.
   * Handles pagination internally to fetch all results.
   * Note: Limited to maxPages iterations to prevent excessive memory/CPU use.
   */
  private static async searchAll(
    medplum: MedplumClient,
    query: PracticeAppointmentTypeQuery,
  ): Promise<PracticeAppointmentType[]> {
    const pageSize = 100;
    const maxPages = 100; // Safeguard to prevent infinite loops on very large result sets
    const allAppointmentTypes: PracticeAppointmentType[] = [];
    let offset = 0;
    let iterations = 0;

    while (true) {
      const appointmentTypes = await PracticeAppointmentType.search(medplum, query, {
        limit: pageSize,
        offset,
      });

      allAppointmentTypes.push(...appointmentTypes);

      // If we got fewer results than the page size, we've fetched everything
      if (appointmentTypes.length < pageSize) {
        break;
      }

      offset += pageSize;
      iterations++;

      // Safeguard: break if we've exceeded max pages to avoid excessive memory/CPU use
      if (iterations >= maxPages) {
        logger.warn(
          `searchAll: Reached maximum page limit (${maxPages}) with ${allAppointmentTypes.length} results. ` +
            `Returning partial results. Query: ${JSON.stringify(query)}`,
        );
        break;
      }
    }

    return allAppointmentTypes;
  }

  /**
   * Search appointment types by practice.
   * Note: Uses default Medplum pagination. For all results, use searchAllByPractice.
   */
  static async searchByPractice(
    medplum: MedplumClient,
    practiceId: string,
  ): Promise<PracticeAppointmentType[]> {
    return PracticeAppointmentType.search(medplum, { practiceId });
  }

  /**
   * Appointment types by id, in one search per chunk.
   *
   * Applies the same `active` and Cal-identifier filters every other search here does, so a caller
   * resolving stored bindings sees exactly what the pickers and the payment map see. A deleted or
   * deactivated id resolves to nothing rather than an error — callers decide what an absent binding
   * means.
   *
   * Chunked at 100: a `Schedule/<uuid>` is ~45 bytes with its comma, and a few hundred in one
   * `_id` param pushes the query string past the ~8KB request-line limit common to proxies.
   */
  static async searchByIds(
    medplum: MedplumClient,
    ids: readonly string[],
  ): Promise<PracticeAppointmentType[]> {
    const uniqueIds = [...new Set(ids)];
    if (uniqueIds.length === 0) return [];

    const chunks: string[][] = [];
    for (let index = 0; index < uniqueIds.length; index += ID_CHUNK_SIZE) {
      chunks.push(uniqueIds.slice(index, index + ID_CHUNK_SIZE));
    }

    const results = await Promise.all(
      chunks.map((chunk) =>
        PracticeAppointmentType.search(medplum, { ids: chunk }, { limit: ID_CHUNK_SIZE }),
      ),
    );
    return results.flat();
  }

  /**
   * Search all appointment types by practice.
   * Handles pagination internally to fetch all results.
   */
  static async searchAllByPractice(
    medplum: MedplumClient,
    practiceId: string,
  ): Promise<PracticeAppointmentType[]> {
    return PracticeAppointmentType.searchAll(medplum, { practiceId });
  }

  /**
   * Search appointment types that allow patient self-scheduling.
   * Fetches all results and filters to self-schedulable types only.
   */
  static async searchSelfSchedulable(
    medplum: MedplumClient,
    practiceId: string,
  ): Promise<PracticeAppointmentType[]> {
    const appointmentTypes = await PracticeAppointmentType.searchAllByPractice(medplum, practiceId);
    return appointmentTypes.filter(
      (appointmentType) => appointmentType.isPatientSelfScheduleEnabled,
    );
  }

  /**
   * Search appointment types available for a specific provider.
   * Note: Uses default Medplum pagination. For all results, use searchAllByProvider.
   *
   * @param medplum - The Medplum client
   * @param providerId - The provider ID to filter by
   * @param practiceId - Optional practice ID to additionally filter by for practice-scoped queries
   */
  static async searchByProvider(
    medplum: MedplumClient,
    providerId: string,
    practiceId?: string,
  ): Promise<PracticeAppointmentType[]> {
    return PracticeAppointmentType.search(medplum, { providerId, practiceId });
  }

  /**
   * Search all appointment types available for a specific provider.
   * Handles pagination internally to fetch all results.
   *
   * @param medplum - The Medplum client
   * @param providerId - The provider ID to filter by
   * @param practiceId - Optional practice ID to additionally filter by for practice-scoped queries
   */
  static async searchAllByProvider(
    medplum: MedplumClient,
    providerId: string,
    practiceId?: string,
  ): Promise<PracticeAppointmentType[]> {
    return PracticeAppointmentType.searchAll(medplum, { providerId, practiceId });
  }

  /**
   * Search self-schedulable appointment types for a specific provider.
   * Fetches all results and filters to only those enabled for patient booking.
   */
  static async searchSelfSchedulableByProvider(
    medplum: MedplumClient,
    providerId: string,
    practiceId?: string,
  ): Promise<PracticeAppointmentType[]> {
    const appointmentTypes = await PracticeAppointmentType.searchAllByProvider(
      medplum,
      providerId,
      practiceId,
    );
    return appointmentTypes.filter(
      (appointmentType) => appointmentType.isPatientSelfScheduleEnabled,
    );
  }

  /**
   * Every payment-gated appointment type across every practice — the abandoned-embed-hold reaper's
   * only way to enumerate which Cal event types can hold an unpaid embed booking pending. There is
   * no FHIR search parameter for "has a linked Stripe/Hint charge", so this fetches every active
   * type globally and filters in-memory.
   */
  static async searchAllRequiringEmbedPaymentHold(
    medplum: MedplumClient,
  ): Promise<PracticeAppointmentType[]> {
    const appointmentTypes = await PracticeAppointmentType.searchAll(medplum, {});
    return appointmentTypes.filter((appointmentType) => appointmentType.requiresEmbedPaymentHold);
  }

  /**
   * Search appointment types that use a specific room.
   * Note: Uses default Medplum pagination. For all results, use searchAllByRoom.
   *
   * @param medplum - The Medplum client
   * @param roomId - The room ID to filter by
   * @param practiceId - Optional practice ID to additionally filter by
   */
  static async searchByRoom(
    medplum: MedplumClient,
    roomId: string,
    practiceId?: string,
  ): Promise<PracticeAppointmentType[]> {
    return PracticeAppointmentType.search(medplum, { roomId, practiceId });
  }

  /**
   * Search all appointment types that use a specific room.
   * Handles pagination internally to fetch all results.
   *
   * @param medplum - The Medplum client
   * @param roomId - The room ID to filter by
   * @param practiceId - Optional practice ID to additionally filter by
   */
  static async searchAllByRoom(
    medplum: MedplumClient,
    roomId: string,
    practiceId?: string,
  ): Promise<PracticeAppointmentType[]> {
    return PracticeAppointmentType.searchAll(medplum, { roomId, practiceId });
  }

  /**
   * Searches for appointment types.
   *
   * @param medplum - The Medplum client
   * @param query - Query parameters for filtering
   * @param options - Search options for pagination and sorting
   * @returns Array of appointment types
   */
  static async search(
    medplum: MedplumClient,
    query: PracticeAppointmentTypeQuery,
    options?: SearchOptions,
  ): Promise<PracticeAppointmentType[]> {
    const resources = await PracticeAppointmentType.searchResources(
      medplum,
      PracticeAppointmentType.toFhirQuery(query),
      options,
    );
    return resources.map((schedule) => new PracticeAppointmentType(schedule));
  }

  /**
   * Searches for appointment types with total count.
   *
   * @param medplum - The Medplum client
   * @param query - Query parameters for filtering
   * @param options - Search options for pagination and sorting
   * @returns Appointment types and total count
   */
  static async searchWithTotal(
    medplum: MedplumClient,
    query: PracticeAppointmentTypeQuery,
    options?: SearchOptions,
  ): Promise<PracticeAppointmentTypeSearchResult> {
    const fhirQuery = PracticeAppointmentType.toFhirQuery(query);
    const [appointmentTypes, total] = await Promise.all([
      PracticeAppointmentType.search(medplum, query, options),
      PracticeAppointmentType.getCount(medplum, fhirQuery, options),
    ]);

    return { appointmentTypes, total };
  }

  /**
   * Subscribe to appointment types based on a given query.
   */
  static subscribe(
    medplum: MedplumClient,
    query: PracticeAppointmentTypeQuery,
    callback: SubscriptionCallback<PracticeAppointmentType>,
  ) {
    return PracticeAppointmentType.subscribeToResource(
      medplum,
      PracticeAppointmentType.toFhirQuery(query),
      (resource, metadata) => callback(new PracticeAppointmentType(resource), metadata),
    );
  }

  //-----------------------------------------------------------------------------------------------
  //                                         FHIR HELPERS
  //-----------------------------------------------------------------------------------------------

  private static toFhirObject(data: CreatePracticeAppointmentTypeData): ResourceData<Schedule> {
    const extensions: Array<
      | { url: string; valueString: string }
      | { url: string; valueInteger: number }
      | { url: string; valueBoolean: boolean }
      | { url: string; valueCode: string }
    > = [
      {
        url: PracticeAppointmentType.Extension.Title,
        valueString: data.title,
      },
      {
        url: PracticeAppointmentType.Extension.Slug,
        valueString: data.slug,
      },
    ];

    if (data.isPatientSelfScheduleEnabled !== undefined) {
      extensions.push({
        url: PracticeAppointmentType.Extension.PatientSelfScheduleEnabled,
        valueBoolean: data.isPatientSelfScheduleEnabled,
      });
    }

    if (data.lengthInMinutes !== undefined) {
      extensions.push({
        url: PracticeAppointmentType.Extension.LengthInMinutes,
        valueInteger: data.lengthInMinutes,
      });
    }

    if (typeof data.maxBookingsPerDay === "number" && data.maxBookingsPerDay > 0) {
      extensions.push({
        url: PracticeAppointmentType.Extension.MaxBookingsPerDay,
        valueInteger: data.maxBookingsPerDay,
      });
    }

    if (data.color !== undefined) {
      extensions.push({
        url: PracticeAppointmentType.Extension.Color,
        valueString: data.color,
      });
    }

    if (data.type !== undefined) {
      extensions.push({
        url: PracticeAppointmentType.Extension.Type,
        valueCode: data.type,
      });
    }

    const locationType = data.locationType ?? PracticeAppointmentType.DefaultLocationType;
    extensions.push({
      url: PracticeAppointmentType.Extension.LocationType,
      valueCode: locationType,
    });

    if (data.calChildEventTypes && data.calChildEventTypes.length > 0) {
      extensions.push({
        url: PracticeAppointmentType.Extension.CalChildEventTypes,
        valueString: JSON.stringify(data.calChildEventTypes),
      });
    }

    if (data.calRoundRobinEventType) {
      extensions.push({
        url: PracticeAppointmentType.Extension.CalRoundRobinEventType,
        valueString: JSON.stringify(data.calRoundRobinEventType),
      });
    }

    // Build actor array: Organization + Providers
    const actors: Array<{ reference: string; display?: string }> = [
      {
        reference: `Organization/${data.practiceId}`,
        display: data.practiceName,
      },
    ];

    if (data.providers && data.providers.length > 0) {
      for (const provider of data.providers) {
        actors.push({
          reference: `Practitioner/${provider.id}`,
          display: provider.name ?? undefined,
        });
      }
    }

    if (data.rooms && data.rooms.length > 0) {
      for (const room of data.rooms) {
        actors.push({
          reference: `Location/${room.id}`,
          display: room.name ?? undefined,
        });
      }
    }

    return {
      identifier: [
        {
          system: PracticeAppointmentType.Identifier.CalTeamEventType,
          value: data.calTeamEventTypeId.toString(),
        },
        {
          system: PracticeAppointmentType.Identifier.Practice,
          value: data.practiceId,
        },
        {
          system: PracticeAppointmentType.Identifier.ScheduleType,
          value: PracticeAppointmentType.ScheduleTypeValue,
        },
      ],
      active: true,
      actor: actors,
      extension: extensions,
      // booking + reminder emails read the description from here, so it must land at create time
      comment: data.description?.trim() || undefined,
    };
  }

  static toFhirQuery(
    query: PracticeAppointmentTypeQuery,
  ): Record<string, string | number | boolean | undefined> {
    const searchParams: Record<string, string | number | boolean | undefined> = {
      active: "true",
      identifier: `${CAL_IDENTIFIER_SYSTEM_TEAM_EVENT_TYPES}|`,
      // `_id`, never a bare `id` — FHIR has no `id` search parameter, and Medplum rejects the
      // request outright rather than ignoring it.
      _id: query.ids?.length ? query.ids.join(",") : undefined,
    };

    // Build actor filters - when multiple actor types are specified,
    // we need AND semantics (appointment type must have ALL specified actors).
    // Arrays in FHIR search use OR semantics, so we must use _filter for AND.
    const filterParts: string[] = [];

    // Count how many actor filters we have
    const actorFilters = [query.practiceId, query.providerId, query.roomId].filter(Boolean);

    if (actorFilters.length > 1) {
      // Use _filter for AND semantics when multiple are specified
      if (query.practiceId) {
        filterParts.push(`actor eq Organization/${query.practiceId}`);
      }
      if (query.providerId) {
        filterParts.push(`actor eq Practitioner/${query.providerId}`);
      }
      if (query.roomId) {
        filterParts.push(`actor eq Location/${query.roomId}`);
      }
    } else if (query.practiceId) {
      searchParams.actor = `Organization/${query.practiceId}`;
    } else if (query.providerId) {
      searchParams.actor = `Practitioner/${query.providerId}`;
    } else if (query.roomId) {
      searchParams.actor = `Location/${query.roomId}`;
    }

    if (filterParts.length > 0) {
      searchParams._filter = filterParts.join(" and ");
    }

    return searchParams;
  }
}

export interface CreatePracticeAppointmentTypeOptions {
  /**
   * Providers to set as available for this appointment type.
   * Expects objects with id and optional name (e.g., `{ id: provider.id, name: provider.name }`).
   * When not provided, no providers will be added to the actor array.
   */
  providers?: Array<{ id: string; name?: string | null }>;
  /**
   * Rooms to set as available for this appointment type.
   * Expects objects with id and optional name (e.g., `{ id: room.id, name: room.name }`).
   * When not provided, no rooms will be added to the actor array.
   */
  rooms?: Array<{ id: string; name?: string | null }>;
  /**
   * Initial selection type for the appointment type.
   */
  type?: "provider" | "room";
  /**
   * Optional first-party appointment type color.
   */
  color?: string;
  /**
   * Whether patients can self-schedule this appointment type.
   */
  isPatientSelfScheduleEnabled?: boolean;
  /**
   * Local mirror of Cal's per-day booking cap (`bookingLimitsCount.day`); omit for no limit.
   */
  maxBookingsPerDay?: number;
}

interface CreatePracticeAppointmentTypeFromEventTypeParams {
  practice: Practice;
  eventType: CalEventType | CalEventType[];
  options?: CreatePracticeAppointmentTypeOptions;
}

/**
 * Create a practice appointment type from a Cal.com team event type.
 *
 * For MANAGED scheduling, Cal.com automatically creates child events for each team member
 * with the same slug. Store child event mappings so clients can request slots by child ID.
 *
 * The actor list includes:
 * - The Organization (practice)
 * - All specified providers (or all practice providers if none specified)
 */
export async function createPracticeAppointmentTypeFromEventType(
  medplum: MedplumClient,
  { practice, eventType, options }: CreatePracticeAppointmentTypeFromEventTypeParams,
): Promise<PracticeAppointmentType> {
  // Handle both single event and array response (for MANAGED scheduling)
  const parentEvent = resolveManagedParentEvent(eventType);
  const childEvents =
    Array.isArray(eventType) && parentEvent?.id
      ? resolveManagedChildEvents(eventType, parentEvent.id)
      : [];

  // Validate parent event exists and has required fields
  if (!parentEvent) {
    throw new Error(
      "Invalid event type response: parent event is missing. Cal.com may have returned an empty array.",
    );
  }

  if (!parentEvent.id) {
    throw new Error(
      `Invalid event type response: parent event is missing ID. Event: ${JSON.stringify(parentEvent)}`,
    );
  }

  if (!parentEvent.title || !parentEvent.slug) {
    throw new Error(
      `Invalid event type response: parent event is missing title or slug. Event ID: ${parentEvent.id}`,
    );
  }

  // Sync only recognized Cal.com location types; unknown vendor tokens should not mutate modality.
  const locations = (parentEvent as { locations?: Array<{ type?: string }> }).locations;
  const locationType =
    Array.isArray(locations) && locations.length > 0 && locations[0]?.type
      ? maybeLocationTypeFromCalLocation(locations[0].type)
      : undefined;

  const calChildEventTypes = childEvents
    .map((child) => {
      if (typeof child.ownerId !== "number" || typeof child.id !== "number") {
        return undefined;
      }

      return {
        calUserId: child.ownerId,
        eventTypeId: child.id,
      };
    })
    .filter((mapping): mapping is CalChildEventTypeMapping => mapping !== undefined);

  return PracticeAppointmentType.create(medplum, {
    practiceId: practice.id,
    practiceName: practice.name,
    calTeamEventTypeId: parentEvent.id,
    title: parentEvent.title,
    slug: parentEvent.slug,
    description: parentEvent.description ?? undefined,
    lengthInMinutes:
      "lengthInMinutes" in parentEvent && typeof parentEvent.lengthInMinutes === "number"
        ? parentEvent.lengthInMinutes
        : undefined,
    isPatientSelfScheduleEnabled: options?.isPatientSelfScheduleEnabled ?? true,
    color: options?.color,
    type: options?.type,
    locationType,
    maxBookingsPerDay: options?.maxBookingsPerDay,
    providers: options?.providers,
    rooms: options?.rooms,
    calChildEventTypes: calChildEventTypes.length > 0 ? calChildEventTypes : undefined,
  });
}
